import { useCallback, useMemo } from 'react';
import { DefaultRootState, useSelector } from 'react-redux';
import { Merge } from 'type-fest';

import { AvailableFeatures, SettingsDto } from 'common/api/autogenerated/client-area';

import { useWeb3 } from 'common/components/Web3/hooks/useWeb3.hook';

import { postLogout } from 'common/redux/user/api';

import { routes } from 'routes/routes.constants';

import { postWeb3SignOut } from 'blocks/Web3Provider/siwe.config';

import { defaultVerificationLimits, VerificationLevels } from './useCustomer.constants';
import { useQuery } from '../useQuery';

export type UserAvailableFeatures = keyof Merge<
  AvailableFeatures,
  Pick<SettingsDto, 'tokenPresaleEnabled' | 'tokenPresalePopupOnLoginEnabled' | 'paymentCardEnabled'>
>;

export const useCustomer = () => {
  const {
    isAuthenticated,
    verificationLimits = defaultVerificationLimits,
    tier,
    tiers,
    id,
    companyName,
    firstName,
    lastName,
    address,
    email,
    settings,
    priceType,
    isMerchant,
    loading,
    verificationLevel,
    intermediateVerificationLevelStatus,
    availableFeatures = {},
  } = useSelector((state: DefaultRootState) => state.common.user);

  const { disconnect } = useWeb3();

  const balance = useSelector((state: DefaultRootState) => state.walletReducer.balance);

  const isTradeOperationEnabled = useMemo(() => {
    const { basic, occasional, pro } = verificationLimits;

    switch (verificationLevel) {
      case VerificationLevels.BASIC:
        return basic;
      case VerificationLevels.OCCASIONAL:
        return occasional;
      case VerificationLevels.PRO:
        return pro;

      default:
        return defaultVerificationLimits.occasional;
    }
  }, [verificationLevel, verificationLimits]);

  const isVerified = useMemo(
    () =>
      verificationLevel === VerificationLevels.BASIC ||
      verificationLevel === VerificationLevels.OCCASIONAL ||
      verificationLevel === VerificationLevels.PRO,
    [verificationLevel],
  );

  const isNotVerified = useMemo(() => verificationLevel === VerificationLevels.NOT_VERIFIED, [verificationLevel]);
  const isBasicVerified = useMemo(() => verificationLevel === VerificationLevels.BASIC, [verificationLevel]);
  const isOccasionalVerified = useMemo(() => verificationLevel === VerificationLevels.OCCASIONAL, [verificationLevel]);
  const isProVerified = useMemo(() => verificationLevel === VerificationLevels.PRO, [verificationLevel]);
  const isDeclined = useMemo(() => verificationLevel === VerificationLevels.DECLINED, [verificationLevel]);
  const isAllowTrade = useMemo(() => isVerified, [isVerified]);

  const isFiatDepositEnabled = useMemo(
    () => isTradeOperationEnabled?.fiat?.isDepositEnabled,
    [isTradeOperationEnabled],
  );
  const isFiatWithdrawalEnabled = useMemo(
    () => isTradeOperationEnabled?.fiat?.isWithdrawalEnabled,
    [isTradeOperationEnabled],
  );
  const isCryptoDepositEnabled = useMemo(
    () => isTradeOperationEnabled?.crypto?.isDepositEnabled,
    [isTradeOperationEnabled],
  );
  const isCryptoWithdrawalEnabled = useMemo(
    () => isTradeOperationEnabled?.crypto?.isWithdrawalEnabled,
    [isTradeOperationEnabled],
  );

  const culture = useSelector((state: DefaultRootState) => state.common.resources.culture);

  const query = useQuery();

  const checkFeaturesAvailability = useCallback(
    (feature: UserAvailableFeatures) => {
      if (query.get(feature) === 'true') return true;

      if (feature in availableFeatures) {
        return Boolean(availableFeatures[feature as keyof AvailableFeatures]);
      }

      return Boolean(settings[feature as keyof SettingsDto]);
    },
    [availableFeatures, query, settings],
  );

  const logOut = useCallback(async () => {
    await postLogout();
    await postWeb3SignOut();
    await disconnect();

    window.location.assign(routes.getWebsite());
  }, [disconnect]);

  const result = useMemo(
    () => ({
      isAllowTrade,
      isVerified,
      isDeclined,

      isNotVerified,
      isBasicVerified,
      isOccasionalVerified,
      isProVerified,

      isFiatDepositEnabled,
      isFiatWithdrawalEnabled,
      isCryptoDepositEnabled,
      isCryptoWithdrawalEnabled,

      intermediateVerificationLevelStatus,

      culture,

      isAuthenticated,
      tier,
      tiers,
      balance,
      verificationLevel,
      email,
      id,
      companyName,
      firstName,
      lastName,
      address,
      settings,
      priceType,
      isMerchant,
      loading,
      checkFeaturesAvailability,
      logOut,
    }),
    [
      isAllowTrade,
      isVerified,
      isDeclined,
      isNotVerified,
      isBasicVerified,
      isOccasionalVerified,
      isProVerified,
      isFiatDepositEnabled,
      isFiatWithdrawalEnabled,
      isCryptoDepositEnabled,
      isCryptoWithdrawalEnabled,
      intermediateVerificationLevelStatus,
      culture,
      isAuthenticated,
      tier,
      tiers,
      balance,
      verificationLevel,
      email,
      id,
      companyName,
      firstName,
      lastName,
      address,
      settings,
      priceType,
      isMerchant,
      loading,
      checkFeaturesAvailability,
      logOut,
    ],
  );

  return result;
};
